<!-- IntegratedImageView.vue -->
<template>
  <div class="integrated-list">
    <div>
      <h4 v-if="title" class="list-title text-sm font-medium text-n-slate-12">{{ title }}</h4>
    </div>

    <!-- Input area -->
    <div class="input-container flex gap-2">
      <input
        type="url"
        v-model="newItemText"
        :placeholder="inputPlaceholder"
        class="list-input flex-1"
        :class="{ 'border-red-500': errorMessage }"
        @keydown.enter.prevent="addItem"
        :disabled="isLoading"
        title="Digite um URL válido de imagem"
      />
      <button 
        v-if="newItemText.length > 0 && !isLoading"
        type="button"
        @click="addItem"
        class="flex items-center justify-center px-3 bg-n-brand text-white border-0 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95 flex-shrink-0"
        style="height: 40px;"
        title="Adicionar imagem"
      >
        ↵
      </button>
    </div>

    <!-- Error message -->
    <p v-if="errorMessage" class="error-message text-sm text-red-500 mt-1 mb-2">
      {{ errorMessage }}
    </p>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="loading-message text-sm text-n-slate-11 mt-1 mb-2">
      <span class="i-lucide-loader animate-spin mr-1 inline-block"></span>
      Verificando imagem...
    </div>

    <!-- Item list -->
    <div v-if="modelValue.length" class="flex flex-wrap gap-2 mt-2">
      <div v-for="(item, index) in modelValue" :key="item.id" class="flex items-center bg-n-slate-4/50 dark:bg-n-slate-7/50 rounded p-1">
        <div 
          class="relative rounded overflow-hidden"
          :class="[
            modalLoading ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer group',
          ]"
          @click="removeItem(index)"
        >
          <img :src="item.url" alt="preview" class="w-36 h-36 object-cover rounded block transition-all duration-200" />
          <div 
            v-if="!modalLoading"
            class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded bg-black-900/70"
          >
            <span class="text-white text-base font-medium">Excluir</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="emptyMessage && !isLoading && !errorMessage" class="empty-container">
      <p class="empty-message">{{ emptyMessage }}</p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import wootAPI from '@/v3/api/apiClient';

export default {
  name: 'ImageUrlList',
  props: {
    modelValue: { type: Array, required: true },
    title: { type: String, default: 'Imagens' },
    inputPlaceholder: { type: String, default: 'Digite a URL da imagem e pressione Enter' },
    emptyMessage: { type: String, default: 'Nenhuma imagem adicionada' },
    modalLoading: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'error'],
  setup(props, { emit }) {
    const newItemText = ref('');
    const isLoading = ref(false);
    const errorMessage = ref('');

    // Valida o formato da URL
    function isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch (_) {
        return false;
      }
    }

    // Verifica se a URL aponta para uma imagem e seu tamanho
    function checkIfImageExists(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          // Check image size by fetching headers
          fetch(url, { method: 'HEAD' })
            .then(response => {
              const contentLength = response.headers.get('content-length');
              if (contentLength) {
                const sizeInMB = parseInt(contentLength) / (1024 * 1024);
                if (sizeInMB > 5) {
                  reject(new Error('A imagem é muito grande. O tamanho máximo permitido é 5MB.'));
                  return;
                }
              }
              resolve(true);
            })
            .catch(() => {
              // If we can't get headers, just proceed (image loaded successfully)
              resolve(true);
            });
        };
        
        img.onerror = () => reject(new Error('A URL não parece ser uma imagem válida.'));
        
        img.src = url;
      });
    }

    const addItem = async () => {
      const url = newItemText.value.trim();
      if (!url) return;

      errorMessage.value = '';

      // Rejeita imagens em formato base64
      if (url.startsWith('data:')) {
        errorMessage.value = 'Formato inválido. Por favor, utilize outro formato.';
        emit('error', errorMessage.value);
        return;
      }

      // Validação do formato da URL
      if (!isValidUrl(url)) {
        errorMessage.value = 'Por favor, insira um URL válido.';
        emit('error', errorMessage.value);
        return;
      }
      
      try {
        isLoading.value = true;
        
        // Verifica se a URL é realmente uma imagem
        await checkIfImageExists(url);
        
        // Se chegou aqui, a URL é uma imagem válida
        emit('update:modelValue', [...props.modelValue, { id: Date.now().toString(), url }]);
        newItemText.value = '';
        
      } catch (error) {
        errorMessage.value = error.message;
        emit('error', error.message);
      } finally {
        isLoading.value = false;
      }
    };

    const getCookie = (name) => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    };

    const getAuthHeaders = () => {
      const cookie = getCookie('cw_d_session_info');
      if (!cookie) return {};
      try {
        const sessionInfo = JSON.parse(cookie);
        return {
          'access-token': sessionInfo['access-token'],
          'client': sessionInfo['client'],
          'uid': sessionInfo['uid']
        };
      } catch (e) {
        console.error('Error parsing session cookie:', e);
        return {};
      }
    };

    const deleteFirebaseImage = async (imageUrl) => {
      try {
        console.log('Attempting to delete Firebase image:', imageUrl);
        const headers = getAuthHeaders();
        console.log('Auth headers:', headers);

        await wootAPI.delete('/api/v1/catalogs/delete_image', {
          params: { image_url: imageUrl },
          headers
        });
        console.log('Successfully deleted Firebase image');
      } catch (error) {
        console.error('Error deleting Firebase image:', error);
      }
    };

    const removeItem = async (index) => {
      // Don't allow removal if modal is loading
      if (props.modalLoading) {
        return;
      }

      const itemToRemove = props.modelValue[index];

      // If it's a Firebase Storage URL, delete it from Firebase
      if (itemToRemove?.url && (itemToRemove.url.includes('firebasestorage.googleapis.com') || itemToRemove.url.includes('storage.googleapis.com'))) {
        await deleteFirebaseImage(itemToRemove.url);
      }
      
      const updated = [...props.modelValue];
      updated.splice(index, 1);
      emit('update:modelValue', updated);
    };

    return { 
      newItemText, 
      addItem, 
      removeItem,
      deleteFirebaseImage,
      getCookie,
      getAuthHeaders,
      isLoading, 
      errorMessage 
    };
  }
};
</script>

<style scoped>
.integrated-list {
  margin-bottom: 20px;
}

.list-title {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 6px 0;
}

.input-container {
  /* Flexbox styling now handled by Tailwind classes */
}

.list-input {
  padding: 10px 12px;
  border: 1px solid rgb(var(--slate-4) / 1);
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  color: rgb(var(--slate-12) / 1);
  height: 40px;
}

.dark .list-input {
  border-color: rgb(var(--slate-7) / 1);
  background-color: rgb(var(--slate-2) / 1);
  color: rgb(var(--slate-12) / 1);
}


.empty-container {
  padding: 12px;
  background-color: rgb(var(--slate-3) / 0.5);
  border-radius: 8px;
  text-align: center;
}

.dark .empty-container {
  background-color: rgb(var(--slate-7) / 0.3);
}

.empty-message {
  color: rgb(var(--slate-10) / 1);
  font-size: 14px;
  margin: 0;
}

.dark .empty-message {
  color: rgb(var(--slate-9) / 1);
}

.error-message {
  margin: 0 0 8px 0;
}
</style>